import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, map } from 'rxjs';
import { ApiService } from '../api/api.service';
import { FilterStateService } from '../filter-state/filter-state.service';
import { StatisticsGranularity, StatisticsQueryResponse } from '../../models/statistics-query.model';
import { normalizeLanguageTag } from '../../utils/language';

export type KnownMetricType =
  | 'puntosControl'
  | 'beacons'
  | 'pantallasSenderos'
  | 'screen_view'
  | 'app_startup'
  | 'menu_interaction'
  | 'form_submission'
  | 'content_feedback'
  | 'registroUsuarios'
  | 'gamificacion';
export type SupportedMetricType = KnownMetricType | (string & {});

export interface ContentSeriesEntry {
  id: string;
  name: string;
  points: Array<[number, number]>;
}

type NamesPayload = { es: string; en?: string; ca?: string; ga?: string; va?: string };

@Injectable({
  providedIn: 'root'
})
export class ContentChartService {
  constructor(
    private readonly apiService: ApiService,
    private readonly translocoSrv: TranslocoService,
    private readonly filterStateService: FilterStateService
  ) {}

  loadSeries(params: {
    projectId: string;
    sourceContentType: string;
    targetContentType: string;
    contentId: string; // 'ALL' or concrete id
    aggregationLevel: 'children' | 'direct';
    metricType: SupportedMetricType;
    metricSubtype: string;
  }): Observable<ContentSeriesEntry[]> {
    const selection = this.resolveSelection(params.projectId);
    const language = normalizeLanguageTag(this.translocoSrv.getActiveLang(), 'es');

    return this.apiService
      .getStatistics({
        project_id: selection.projectId,
        content_id: params.contentId,
        source_content_type: params.sourceContentType,
        target_content_type: params.targetContentType,
        aggregation_level: params.aggregationLevel,
        metric_type: params.metricType,
        metric_subtype: params.metricSubtype,
        startDate: selection.startDate,
        endDate: selection.endDate,
        granularity: selection.granularity,
        language
      })
      .pipe(map((response) => this.responseToSeries(response)));
  }

  private responseToSeries(response: StatisticsQueryResponse): ContentSeriesEntry[] {
    return [...this.responseToMap(response).values()].sort((a, b) => a.name.localeCompare(b.name));
  }

  private responseToMap(response: StatisticsQueryResponse): Map<string, ContentSeriesEntry> {
    const mapData = new Map<string, ContentSeriesEntry>();

    for (const series of response.data || []) {
      const id = series.content.id;
      const name = this.resolveContentName(series.content.name as NamesPayload, id);
      const points: Array<[number, number]> = (series.data || [])
        .map((point) => [new Date(point.dateTime).getTime(), Number(point.value || 0)] as [number, number])
        .sort((x, y) => x[0] - y[0]);

      mapData.set(id, { id, name, points });
    }

    return mapData;
  }

  private resolveContentName(names: NamesPayload, fallbackId: string): string {
    const activeLang = this.translocoSrv.getActiveLang();
    // TOFIX-TYPE: NamesPayload should support dynamic language keys
    const byLanguage = (names as any)?.[activeLang];
    return byLanguage || names?.es || names?.en || names?.ca || names?.ga || names?.va || fallbackId;
  }

  private resolveSelection(projectId: string): {
    projectId: string;
    granularity: StatisticsGranularity;
    startDate: string;
    endDate: string;
  } {
    const snapshot = this.filterStateService.getSnapshot();
    const now = new Date();

    const year = snapshot.selectedYear ?? now.getUTCFullYear();
    const month = snapshot.selectedMonth ?? now.getUTCMonth() + 1;
    const day = snapshot.selectedDay;

    let granularity: StatisticsGranularity = 'daily';
    let start: Date;
    let end: Date;

    if (day !== null) {
      granularity = 'hourly';
      start = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
      end = new Date(Date.UTC(year, month - 1, day + 1, 0, 0, 0));
    } else {
      granularity = 'daily';
      start = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0));
      end = new Date(Date.UTC(year, month, 1, 0, 0, 0));
    }

    return {
      projectId: projectId || snapshot.projectId || '',
      granularity,
      startDate: start.toISOString(),
      endDate: end.toISOString()
    };
  }
}
